import {
  DEFAULT_SUMMARY_STATE,
  SUMMARY_STORAGE_PREFIX,
} from "./table.summary.constants"
import type {
  SummaryAggregations,
  TableSummaryState,
} from "./table.summary.types"
import { isSummaryAggregationType } from "./table.summary.utils"

/**
 * The remembered summary of a table, or the default when there is none (or
 * no `tableId` to remember it under). Anything that is not a known
 * calculation is dropped on the way in, so a stale entry can never put a cell
 * in a state its menu cannot express.
 */
export function readSummaryState(
  tableId: string | undefined,
): TableSummaryState {
  if (!tableId) return DEFAULT_SUMMARY_STATE
  try {
    const raw = localStorage.getItem(SUMMARY_STORAGE_PREFIX + tableId)
    if (!raw) return DEFAULT_SUMMARY_STATE

    const parsed = JSON.parse(raw) as Partial<TableSummaryState>
    const aggregations: SummaryAggregations = {}
    for (const [columnId, type] of Object.entries(parsed.aggregations ?? {})) {
      if (isSummaryAggregationType(type)) aggregations[columnId] = type
    }
    return { isVisible: parsed.isVisible === true, aggregations }
  } catch {
    return DEFAULT_SUMMARY_STATE
  }
}

/** Remembers the summary; a default (hidden, nothing set) leaves no trace. */
export function writeSummaryState(tableId: string, state: TableSummaryState) {
  const key = SUMMARY_STORAGE_PREFIX + tableId
  const isDefault =
    !state.isVisible && Object.keys(state.aggregations).length === 0
  try {
    if (isDefault) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(state))
  } catch {
    // Storage full or unavailable: the summary still works for the session.
  }
}
